import { useEffect, useRef, useState } from "react";
import {
  useV2ListPsaIntegrationsQuery,
  useV2RefreshPsaIntegrationSettingsMutation,
} from "@generated";
import { ResolvedIntegration } from "../types/PsaProject";

export const REFRESH_POLL_MS = 3000;
// The worker calls the PSA once per reference list; a slow on-prem ConnectWise
// can take tens of seconds. Past this, stop polling and tell the user to check
// back rather than spinning forever on a worker that failed outright.
export const REFRESH_TIMEOUT_MS = 90000;

export type RefreshStatus =
  | "idle"
  | "refreshing"
  | "refreshed"
  | "timedOut"
  | "failed";

// The refresh endpoint only enqueues PsaSettingsRefreshWorker (202). Completion
// is observed by polling the integration list -- the same cache entry
// usePsaIntegrations reads, so `integration` re-renders with the new settings --
// until settings.refreshed_at moves off the value captured at click time.
export const useRefreshPsaSettings = (integration: ResolvedIntegration) => {
  const [trigger] = useV2RefreshPsaIntegrationSettingsMutation();
  const [status, setStatus] = useState<RefreshStatus>("idle");
  const [error, setError] = useState("");
  const startedFrom = useRef<string | undefined>(undefined);
  const refreshedAt = integration.settings.refreshed_at;
  const polling = status === "refreshing";

  useV2ListPsaIntegrationsQuery(undefined, {
    skip: !polling,
    pollingInterval: polling ? REFRESH_POLL_MS : 0,
  });

  useEffect(() => {
    if (polling && refreshedAt !== startedFrom.current) {
      setStatus("refreshed");
    }
  }, [polling, refreshedAt]);

  useEffect(() => {
    if (!polling) return;
    const timer = setTimeout(() => setStatus("timedOut"), REFRESH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [polling]);

  const refresh = async () => {
    startedFrom.current = refreshedAt;
    setError("");
    try {
      await trigger({ id: integration.id }).unwrap();
      setStatus("refreshing");
    } catch (err: any) {
      setError(
        err?.data?.errors?.[0]?.detail || "PSA settings could not be refreshed."
      );
      setStatus("failed");
    }
  };

  return { status, error, refresh };
};
